import axios from 'axios';

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor to add JWT token
apiClient.interceptors.request.use(
  (config) => {
    // Only add landlord token if Authorization header is not already set
    // (tenant API calls set their own headers via getAuthHeaders)
    if (!config.headers.Authorization) {
      const token = localStorage.getItem('landlord_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle 401 errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Check if this is a tenant API call by looking at the URL or current path
      const isCurrentlyTenantPortal = window.location.pathname.includes('/tenant') || 
                                     window.location.pathname.includes('/o/');
      const isTenantApiCall = error.config?.url?.includes('/tenant/') || 
                             error.config?.url?.includes('/auth/tenant/');
      
      if (isCurrentlyTenantPortal || isTenantApiCall) {
        // Handle tenant 401 - clear tenant tokens but don't redirect to landlord login
        localStorage.removeItem('tenant_token');
        localStorage.removeItem('tenant_id');
        localStorage.removeItem('tenant_data');
        sessionStorage.removeItem('tenant_token');
        sessionStorage.removeItem('tenant_id');
        sessionStorage.removeItem('tenant_data');
        // Stay on current tenant page - let tenant login form handle it
      } else {
        // Handle landlord 401 - clear landlord tokens and redirect to landlord login
        localStorage.removeItem('landlord_token');
        localStorage.removeItem('landlord_user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// ===== Property APIs =====
export const propertyAPI = {
  getAll: async () => (await apiClient.get('/api/properties')).data,
  getAllEnhanced: async () => (await apiClient.get('/api/properties/enhanced')).data,
  create: async (data) => (await apiClient.post('/api/properties', data)).data,
  update: async (id, data) => (await apiClient.put(`/api/properties/${id}`, data)).data,
  delete: async (id) => (await apiClient.delete(`/api/properties/${id}`)).data,
  uploadImage: async (id, file) => {
    const fd = new FormData();
    fd.append('image', file);
    return (await apiClient.post(`/api/upload/property-image/${id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })).data;
  }
};

// ===== Tenant APIs =====
export const tenantAPI = {
  getAll: async () => (await apiClient.get('/api/tenants')).data,
  create: async (data, sendEmail = false) => (await apiClient.post(`/api/tenants?send_email=${sendEmail}`, data)).data,
  update: async (id, data) => (await apiClient.put(`/api/tenants/${id}`, data)).data,
  delete: async (id) => (await apiClient.delete(`/api/tenants/${id}`)).data
};

// ===== Payment APIs =====
export const paymentAPI = {
  getAll: async () => (await apiClient.get('/api/payments')).data,
  create: async (data) => (await apiClient.post('/api/payments', data)).data,
  update: async (id, data) => (await apiClient.put(`/api/payments/${id}`, data)).data,
  getOverdue: async () => (await apiClient.get('/api/payments/overdue')).data
};

// ===== Maintenance APIs =====
export const maintenanceAPI = {
  getAll: async () => (await apiClient.get('/api/maintenance')).data,
  create: async (data) => (await apiClient.post('/api/maintenance', data)).data,
  update: async (id, data) => (await apiClient.put(`/api/maintenance/${id}`, data)).data,
  delete: async (id) => (await apiClient.delete(`/api/maintenance/${id}`)).data,
  assignContractor: async (id, contractorId) => (await apiClient.put(`/api/maintenance/${id}/assign`, { contractor_id: contractorId })).data,
  completeRequest: async (id, completionData) => (await apiClient.put(`/api/maintenance/${id}/complete`, completionData)).data,
  uploadImage: async (id, file) => {
    const fd = new FormData();
    fd.append('image', file);
    return (await apiClient.post(`/api/upload/maintenance-image/${id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })).data;
  },
  getNotifications: async (id) => (await apiClient.get(`/api/maintenance/${id}/notifications`)).data,
  resendNotification: async (notificationId) => (await apiClient.post(`/api/maintenance/notifications/${notificationId}/resend`)).data
};

// ===== Contractor APIs =====
export const contractorAPI = {
  getAll: async () => (await apiClient.get('/api/contractors')).data,
  create: async (data) => (await apiClient.post('/api/contractors', data)).data,
  update: async (id, data) => (await apiClient.put(`/api/contractors/${id}`, data)).data,
  getCompletedJobs: async (contractorId) => (await apiClient.get(`/api/contractor/${contractorId}/completed-jobs`)).data,
  getCompletedJobs: async (contractorId) => (await apiClient.get(`/api/contractor/${contractorId}/completed-jobs`)).data
};

// ===== Dashboard APIs =====
export const dashboardAPI = {
  getStats: async () => (await apiClient.get('/api/dashboard/stats')).data
};

// ===== Enhanced APIs =====
export const enhancedAPI = {
  getDashboardData: async () => {
    const [stats, properties, payments, maintenanceRequests] = await Promise.all([
      dashboardAPI.getStats(),
      propertyAPI.getAll(),
      paymentAPI.getAll(),
      maintenanceAPI.getAll()
    ]);
    
    return {
      stats,
      properties,
      payments,
      maintenanceRequests
    };
  },
  
  getTenantsWithProperties: async () => {
    const [tenants, properties] = await Promise.all([
      tenantAPI.getAll(),
      propertyAPI.getAll()
    ]);
    
    return tenants.map(tenant => ({
      ...tenant,
      property: properties.find(p => p.id === tenant.property_id),
      propertyName: properties.find(p => p.id === tenant.property_id)?.name || 'Unknown'
    }));
  },
  
  getPaymentsWithDetails: async () => {
    const [payments, tenants, properties] = await Promise.all([
      paymentAPI.getAll(),
      tenantAPI.getAll(), 
      propertyAPI.getAll()
    ]);
    
    return payments.map(payment => {
      const tenant = tenants.find(t => (t.id || t._id) === payment.tenant_id);
      const property = properties.find(p => (p.id || p._id) === tenant?.property_id);
      return {
        ...payment,
        tenantName: tenant?.name || 'Unknown',
        propertyName: property?.name || 'Unknown',
        unitNumber: tenant?.unit || 'N/A',
        method: payment.payment_method || 'Unknown', // Map payment_method to method
        paid_date: payment.payment_date || payment.paid_date, // Ensure consistent date field
        transaction_id: payment.transaction_id || payment.stripe_payment_intent_id || '-'
      };
    });
  },
  
  // Add properties getter for Documents page
  getProperties: async () => propertyAPI.getAll()
};

// ===== Tenant Portal APIs =====
export const tenantPortalAPI = {
  login: async (credentials) => {
    const response = await apiClient.post('/api/auth/tenant/token', credentials);
    const token = response.data.access_token;
    
    // Get tenant details for display
    const tenantData = {
      name: credentials.tenant_name || `Tenant ${credentials.tenant_id.substring(0, 8)}`,
      email: credentials.tenant_email || 'tenant@cozykeys.com',
      role: 'Tenant',
      tenant_id: credentials.tenant_id
    };
    
    // Store token and tenant data based on remember preference
    if (credentials.remember) {
      localStorage.setItem('tenant_token', token);
      localStorage.setItem('tenant_id', credentials.tenant_id);
      localStorage.setItem('tenant_data', JSON.stringify(tenantData));
    } else {
      sessionStorage.setItem('tenant_token', token);
      sessionStorage.setItem('tenant_id', credentials.tenant_id);
      sessionStorage.setItem('tenant_data', JSON.stringify(tenantData));
    }
    
    return response.data;
  },
  
  revoke: async () => {
    localStorage.removeItem('tenant_token');
    localStorage.removeItem('tenant_id');
    sessionStorage.removeItem('tenant_token');
    sessionStorage.removeItem('tenant_id');
  },
  
  getAuthHeaders: () => {
    const token = localStorage.getItem('tenant_token') || sessionStorage.getItem('tenant_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  },
  
  history: async () => {
    const headers = tenantPortalAPI.getAuthHeaders();
    return (await apiClient.get('/api/tenant/payments/history', { headers })).data;
  },
  
  myMaintenance: async () => {
    const headers = tenantPortalAPI.getAuthHeaders();
    return (await apiClient.get('/api/tenant/maintenance/my', { headers })).data;
  },
  
  submitMaintenance: async (data) => {
    const headers = tenantPortalAPI.getAuthHeaders();
    return (await apiClient.post('/api/tenant/maintenance', data, { headers })).data;
  },
  
  deleteMaintenance: async (requestId) => {
    const headers = tenantPortalAPI.getAuthHeaders();
    return (await apiClient.delete(`/api/tenant/maintenance/${requestId}`, { headers })).data;
  },
  
  createStripeIntent: async (data) => {
    const headers = tenantPortalAPI.getAuthHeaders();
    return (await apiClient.post('/api/payments/create-intent', data, { headers })).data;
  },
  
  paypalCreateOrder: async (data) => {
    const headers = tenantPortalAPI.getAuthHeaders();
    return (await apiClient.post('/api/integrations/paypal/order', data, { headers })).data;
  },
  
  paypalCapture: async (data) => {
    const headers = tenantPortalAPI.getAuthHeaders();
    return (await apiClient.post('/api/integrations/paypal/capture', data, { headers })).data;
  },
  
  getOrgSettings: async (orgId) => (await apiClient.get(`/api/org/${orgId}/settings`)).data,
  
  // New profile and document APIs
  getTenants: async () => (await apiClient.get('/api/tenants')).data,
  getProperties: async () => (await apiClient.get('/api/properties')).data,
  updateProfile: async (tenantId, data) => (await apiClient.put(`/api/tenants/${tenantId}`, data)).data,
  getDocuments: async (tenantId) => {
    const token = localStorage.getItem('tenant_token') || sessionStorage.getItem('tenant_token');
    return (await apiClient.get(`/api/tenant/documents`, { headers: { Authorization: `Bearer ${token}` } })).data;
  },
  
  // Autopay API endpoints
  getAutopaySettings: async () => {
    const token = localStorage.getItem('tenant_token') || sessionStorage.getItem('tenant_token');
    if (!token) throw new Error('No tenant token found');
    return (await apiClient.get(`/api/tenant/autopay`, { headers: { Authorization: `Bearer ${token}` } })).data;
  },
  
  setupAutopay: async (settings) => {
    const token = localStorage.getItem('tenant_token') || sessionStorage.getItem('tenant_token');
    if (!token) throw new Error('No tenant token found');
    return (await apiClient.post(`/api/tenant/autopay`, settings, { headers: { Authorization: `Bearer ${token}` } })).data;
  },
  
  disableAutopay: async () => {
    const token = localStorage.getItem('tenant_token') || sessionStorage.getItem('tenant_token');
    if (!token) throw new Error('No tenant token found');
    return (await apiClient.delete(`/api/tenant/autopay`, { headers: { Authorization: `Bearer ${token}` } })).data;
  }
};

// ===== Leases & Reports APIs =====
export const leasesAPI = {
  getAll: async () => (await apiClient.get('/api/leases')).data,
  create: async (data) => (await apiClient.post('/api/leases', data)).data,
  update: async (id, data) => (await apiClient.put(`/api/leases/${id}`, data)).data,
  delete: async (id) => (await apiClient.delete(`/api/leases/${id}`)).data,
  uploadDoc: async (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return (await apiClient.post(`/api/upload/lease-doc/${id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })).data;
  }
};

export const reportsAPI = {
  get: async (key) => (await apiClient.get(`/api/reports/${key}`)).data,
  csv: async (key) => {
    const res = await apiClient.get(`/api/reports/${key}?csv=true`, { responseType: 'blob' });
    return res.data;
  }
};

// ===== Autopay & Collections APIs =====
export const autopayAPI = {
  // Late Fee Rules Management
  getLateFeeRules: async () => (await apiClient.get('/api/late-fee-rules')).data,
  createLateFeeRule: async (rule) => (await apiClient.post('/api/late-fee-rules', rule)).data,
  updateLateFeeRule: async (id, rule) => (await apiClient.put(`/api/late-fee-rules/${id}`, rule)).data,
  deleteLateFeeRule: async (id) => (await apiClient.delete(`/api/late-fee-rules/${id}`)).data,
  
  // Collections Processing
  processAutopay: async () => (await apiClient.post('/api/collections/process-autopay')).data,
  sendReminders: async () => (await apiClient.post('/api/collections/send-reminders')).data,
  applyLateFees: async () => (await apiClient.post('/api/collections/apply-late-fees')).data,
  
  // Get autopay statistics and overview
  getAutopayOverview: async () => {
    // This would typically be a dedicated endpoint, but we'll construct it from existing data
    try {
      const [tenants, payments] = await Promise.all([
        apiClient.get('/api/tenants'),
        apiClient.get('/api/payments')
      ]);
      
      // Calculate basic stats
      const autopayTenants = 0; // Would need actual autopay settings count
      const monthlyVolume = 0; // Would calculate from recent autopay payments
      const failedAttempts = 0; // Would count failed autopay attempts
      
      return {
        autopayTenants,
        monthlyVolume,
        failedAttempts
      };
    } catch (error) {
      console.error('Error fetching autopay overview:', error);
      return { autopayTenants: 0, monthlyVolume: 0, failedAttempts: 0 };
    }
  }
};

// ===== Notifications APIs =====
export const notificationAPI = {
  getAll: async (limit = 50, skip = 0) => (await apiClient.get(`/api/notifications?limit=${limit}&skip=${skip}`)).data,
  getUnread: async () => (await apiClient.get('/api/notifications/unread')).data,
  create: async (notification) => (await apiClient.post('/api/notifications', notification)).data,
  markAsRead: async (id) => (await apiClient.put(`/api/notifications/${id}/read`)).data,
  delete: async (id) => (await apiClient.delete(`/api/notifications/${id}`)).data,
};

// ===== Maintenance Estimates APIs =====
export const maintenanceEstimateAPI = {
  getByRequestId: async (requestId) => (await apiClient.get(`/api/maintenance/${requestId}/estimates`)).data,
  create: async (requestId, estimate, contractorId) => 
    (await apiClient.post(`/api/maintenance/${requestId}/estimates?contractor_id=${contractorId}`, estimate)).data,
  approve: async (estimateId, approval) => 
    (await apiClient.put(`/api/maintenance/estimates/${estimateId}/approval`, approval)).data,
};

export default apiClient;